import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { OrganizationService } from '../services/organization.service';

export interface BankAccountOption {
  id: string;
  label: string;
  bsb?: string;
  account_number?: string;
}

export interface LenderOption {
  id: string;
  name: string;
}

/**
 * Loads bank accounts for the current org.
 * Use in any dropdown that needs a bank account picker.
 */
export function useBankAccounts() {
  const [accounts, setAccounts] = useState<BankAccountOption[]>([]);

  useEffect(() => {
    (async () => {
      const orgId = await OrganizationService.getOrganizationId();
      if (!orgId) return;
      const { data } = await supabase
        .from('bank_accounts')
        .select('id, account_name, bsb, account_number, bank_name, is_default')
        .eq('organization_id', orgId)
        .eq('is_disabled', false)
        .order('is_default', { ascending: false });
      setAccounts((data || []).map(a => ({
        id: a.id,
        label: `${a.account_name}${a.bank_name ? ` (${a.bank_name})` : ''}${a.is_default ? ' ★' : ''}`,
        bsb: a.bsb,
        account_number: a.account_number,
      })));
    })();
  }, []);

  return accounts;
}

/**
 * Loads finance lenders for the current org.
 * Use in any dropdown that needs a lender picker.
 */
export function useFinanceLenders() {
  const [lenders, setLenders] = useState<LenderOption[]>([]);

  const refresh = async () => {
    const orgId = await OrganizationService.getOrganizationId();
    if (!orgId) return;
    const { data } = await supabase
      .from('finance_lenders')
      .select('id, name')
      .eq('organization_id', orgId)
      .eq('is_active', true)
      .order('display_order');
    setLenders(data || []);
  };

  const addLender = async (name: string) => {
    const orgId = await OrganizationService.getOrganizationId();
    if (!orgId) return;
    await supabase
      .from('finance_lenders')
      .insert({ organization_id: orgId, name, display_order: lenders.length + 1 });
    await refresh();
  };

  useEffect(() => { refresh(); }, []);

  return { lenders, addLender, refresh };
}
